/**
 * SW2-2458 (cellular follow-up). API requests made for what the user is doing, so the background
 * photo queue can step aside for them.
 *
 * On a phone's cellular uplink, an upload fills the send buffer: every request made while it runs —
 * a form's dropdown options, a map's data — waits behind the photo bytes already queued. The
 * background queue therefore sends one photo per request and, before each, waits here until no
 * foreground request is in flight (`waitForForegroundIdle`).
 *
 * Counted by `xhrService`'s interceptors. Requests flagged `backgroundUpload` are not counted.
 */

let active = 0;
let lastSettled = -Infinity;

/** Monotonic, unlike Date.now(): a system clock change cannot stretch or skip the waits. */
const now = () => performance.now();

/** Count a request as in flight. Returns its release, which is safe to call more than once. */
export function beginForegroundRequest(): () => void {
  let released = false;

  active += 1;

  return () => {
    if (released) return;
    released = true;
    active = Math.max(0, active - 1);
    lastSettled = now();
  };
}

export function activeForegroundRequests(): number {
  return active;
}

export type IdleOptions = {
  /**
   * How long nothing must have been in flight. Forms chain requests (one answer decides the next
   * request), so the moment the count reaches 0 is often just a gap in a chain.
   */
  quietMs?: number;
  /**
   * Go ahead regardless after this long, so a polling widget or a request that never settles
   * cannot hold photos back indefinitely.
   */
  maxWaitMs?: number;
  pollMs?: number;
};

/** Resolves once no foreground request has been in flight for `quietMs`, or after `maxWaitMs`. */
export async function waitForForegroundIdle({
  quietMs = 500,
  maxWaitMs = 10000,
  pollMs = 100,
}: IdleOptions = {}): Promise<void> {
  const start = now();

  while (now() - start < maxWaitMs) {
    if (active === 0 && now() - lastSettled >= quietMs) return;

    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }

  // maxWaitMs reached with requests still in flight (or settling): go ahead regardless.
  return;
}

/** Tests only. */
export function resetForegroundRequests() {
  active = 0;
  lastSettled = -Infinity;
}
